import "./style.scss";

import React, {FC, useContext, useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import Alert from "shared/ui/Alert";
import Button from "shared/ui/Button";
import {Button_Style, Button_Type} from "shared/ui/Button/Button";
import Input from "shared/ui/Input";
import {UserContext} from "features/account";
import {login} from "features/auth";
import { delete_files } from "features/ai-assistant";
import { ASSISTANCE_PRICING } from "shared/config/pricings";
import { ReactComponent as IconBack } from "assets/img/icons/icon_arrow-Left.svg";
import { ReactComponent as IconNext } from "assets/img/icons/icon_arrow-Right.svg";
import FranceIdentiteImg from "assets/img/france-identite.png";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FranceIdentiteSso } from "components/FranceIdentiteSSO/FranceIdentiteSSO";

type LoginFormProps = {
    forOnlyPartner?: boolean;
    forADVEnableSSO?: boolean;
    isNewUserFromWordpressAccompaniment?: boolean;
    offreIdFromWordpress?: number;
}

const LoginForm: FC<LoginFormProps> = ({forOnlyPartner = false, forADVEnableSSO=true,  isNewUserFromWordpressAccompaniment = false, offreIdFromWordpress}) => {
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [userType, setUserType] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const { user, setAi_conversation, get_ai_thread} = useContext(UserContext);
    const [userLastAccompanimentPaid, setUserLastAccompanimentPaid] = useState<any>(ASSISTANCE_PRICING[1]);
    const [email, setEmail] = useState(""); // Pour persister l'email entre les étapes

    const location = useLocation();

    // État des steps
    const [steps, setSteps] = useState([
        { isCurrent: true, isDone: false },  // Étape 1: Email
        { isCurrent: false, isDone: false }, // Étape 2: Password
    ]);

    useEffect(() => {
        // Check if the URL contains "professionnels"
        const hasProfessionnels = location.pathname.includes("professionnels");
        setUserType(hasProfessionnels ? "professionnels" : "particuliers");
    }, [location]);

    useEffect(() => {
        const getOffer = async () => {
            try {
                ASSISTANCE_PRICING.map((offer) => {
                    if (offreIdFromWordpress === offer.offreId) {
                        setUserLastAccompanimentPaid(offer);
                    }
                })
            } catch (error) {
                console.log(error);
            }
        };

        if (offreIdFromWordpress) {
            getOffer();
        }
    }, [offreIdFromWordpress]);

    const handlePreviousStep = () => {
        const currentStep = steps.findIndex((step) => step.isCurrent);
        if (currentStep > 0) {
            handleUpdateStep(currentStep - 1);
        }
    };

    const handleUpdateStep = (step: number) => {
        setSteps(
          steps.map((s, index) => {
              if (index === step) {
                  return { isCurrent: true, isDone: false };
              } else if (index < step) {
                  return { isCurrent: false, isDone: true };
              }
              return { isCurrent: false, isDone: false };
          }),
        );
    };

    const handleNextStep = () => {
        const currentStep = steps.findIndex((step) => step.isCurrent);
        if (currentStep < steps.length - 1) {
            handleUpdateStep(currentStep + 1);
        }
    };

    // Schéma de validation pour l'étape 1 (email)
    const validationSchemaStep1 = Yup.object({
        email: Yup.string().email("Email invalide").required("L'email est requis"),
    });

    // Schéma de validation pour l'étape 2 (password)
    const validationSchemaStep2 = Yup.object({
        password: Yup.string().required("Le mot de passe est requis"),
    });

    // Formik pour l'étape 1
    const formikStep1 = useFormik({
        initialValues: {
            email: "",
        },
        validationSchema: validationSchemaStep1,
        onSubmit: async (values) => {
            // Persister l'email et passer à l'étape 2
            setEmail(values.email);
            handleNextStep();
        },
    });

    // Formik pour l'étape 2 (connexion finale)
    const formikStep2 = useFormik({
        initialValues: {
            password: "",
        },
        validationSchema: validationSchemaStep2,
        onSubmit: async (values) => {
            setIsLoading(true);

            try {
                // Utiliser l'email persisté de l'étape 1
                const request: any = await login(email, values.password, isNewUserFromWordpressAccompaniment, offreIdFromWordpress);
                setIsLoading(false);

                // Check if MFA is required
                if (request.mfaRequired) {
                    if (request.mfaType === 'email') {
                        navigate('/mfa/verify', {
                            state: { mfaToken: request.mfaToken, mfaType: 'email', mfaEmail: request.mfaEmail }
                        });
                    } else if (request.mfaType === 'mobile') {
                        navigate('/mfa/verify-code', {
                            state: { mfaToken: request.mfaToken, mfaType: 'mobile', mfaEmail: request.mfaEmail }
                        });
                    }
                    else if (request.mfaType === 'idprotect') {
                        navigate('/mfa/waiting', {
                            state: { mfaToken: request.mfaToken, mfaType: 'idprotect', mfaEmail: request.mfaEmail }
                        });
                    }
                } else {
                    // Normal login
                    // Show MFA proposal if flag is true
                    if (request.showMFAProposal) {
                        // Layout component will handle showing the modal
                        localStorage.setItem('showMFAProposal', 'true');
                    }

                    if (request?.type === "individual") {
                        await delete_files(user?.clientNumber!);
                        localStorage.removeItem("assistantConversation");
                        setAi_conversation([]);
                        if (request?.mustRedirectToIDAssistant) {
                            navigate("/particuliers/id-assistant/add/standard");
                        } else {
                            navigate("/particuliers/dashboard");
                        }

                    } else if (request?.type === "partner") {
                        if (!!+(process.env.REACT_APP_FOR_PARTNER_ONLY||0)){
                            navigate("/professionnels/base-de-donnees");
                        }
                        else{
                            navigate("/professionnels/dashboard")
                        }
                    }
                    else {
                        setErrorMessage(request.response.data.error);
                    }
                }
            } catch (error: any) {
                setIsLoading(false);
                if (error.request.response?.startsWith("<")) {
                    setErrorMessage("Le serveur est indisponible. Merci de réessayer plus tard.");
                } else {
                    const errorText = JSON.parse(error.request.response).error;
                    setErrorMessage(errorText);
                }
            }
        },
    });

    const handleRedirect = (): any => {
        navigate(`/${userType}/register`);
    };

    const handleForgetPassword = (): any => {
        navigate("/resend");
    };

    const handleConfirmationEmail = (): any => {
        navigate("/commons/confirmation");
    };

    return (
      <>
          <div className={"d-flex flex-column justify-content-center align-items-center card"}>
              <h5>Espace {userType}</h5>
              <h2>Connexion</h2>
              {isNewUserFromWordpressAccompaniment && (
                <h5 className={"text-danger text-center text-initial"}>Suite à votre souscription <span
                  className={"text-danger bold"}>{userLastAccompanimentPaid?.title}</span></h5>
              )}
              {errorMessage && <Alert type="error">{errorMessage}</Alert>}

              {/* Étape 1: Email */}
              {steps[0].isCurrent && (
                <form onSubmit={formikStep1.handleSubmit} className={"w-100 py-2"}>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email"
                      required
                      onChange={formikStep1.handleChange}
                      onBlur={formikStep1.handleBlur}
                      value={formikStep1.values.email}
                      errorMessage={formikStep1.touched.email && formikStep1.errors.email ? formikStep1.errors.email : ""}
                      data-testid="email-input"
                    />
                    <Input
                      type="password"
                      name="password"
                      placeholder="Mot de passe"
                      data-testid="password-input"
                      className={"display-none"}
                    />



                    <div className="d-flex justify-content-center w-100">
                        <Button
                          buttonType="submit"
                          label="Connexion"
                          type={userType === "professionnels" ? Button_Type.SECONDARY : Button_Type.PRIMARY}
                          disabled={!formikStep1.isValid || !formikStep1.dirty}
                          // icon={<IconNext className="PageHeader__icon" fill={"#ffffff"} />}
                          data-testid="next-button"
                          className={"w-100 d-flex flex-row justify-content-center text-sm"}
                        />
                    </div>

                    {userType !== "professionnels" && (
                      <>
                          {/*France Identité SSO here*/}
                          <FranceIdentiteSso/>
                      </>
                    )}

                    <div className="mt-7 d-flex flex-column align-items-center">
                        {!forOnlyPartner && (
                          <div className={"d-flex flex-row align-items-center justify-content-center"}>
                              <span className={"text-sm text-muted-foreground"}>Vous n’avez pas de compte?</span>
                              <Button
                                label="S'inscrire"
                                type={Button_Type.LINK}
                                className="m-auto mb-1 text-sm px-1 text-initial"
                                onClick={() => handleRedirect()}
                              />
                          </div>
                        )}
                        <Button
                          label="Confirmation de votre email?"
                          type={Button_Type.LINK}
                          className="m-auto my-5 text-sm"
                          onClick={(e) => handleConfirmationEmail()}
                        />
                    </div>
                </form>
                  )}

              {/* Étape 2: Password */}
              {steps[1].isCurrent && (
                <form onSubmit={formikStep2.handleSubmit} className={"w-100 py-2"}>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email"
                      data-testid="email-input"
                      className={"display-none"}
                    />
                    <Input
                      type="password"
                      name="password"
                      placeholder="Mot de passe"
                      required
                      onChange={formikStep2.handleChange}
                      onBlur={formikStep2.handleBlur}
                      value={formikStep2.values.password}
                      errorMessage={formikStep2.touched.password && formikStep2.errors.password ? formikStep2.errors.password : ""}
                      data-testid="password-input"
                    />

                    <div className="mt-1 d-flex justify-content-between w-100 gap-1 flex-wrap-reverse">
                        <Button
                          className="font-S"
                          label={"Retour"}
                          onClick={() => handlePreviousStep()}
                          type={userType === "professionnels" ? Button_Type.SECONDARY : Button_Type.PRIMARY}
                          style={Button_Style.OUTLINED}
                          // icon={<IconBack className="PageHeader__icon" fill={"#ffffff"} />}
                        />
                        <Button
                          buttonType="submit"
                          label="Connexion"
                          type={userType === "professionnels" ? Button_Type.SECONDARY : Button_Type.PRIMARY}
                          disabled={!formikStep2.isValid || !formikStep2.dirty}
                          data-testid="connexion-button"
                          isLoader={isLoading && !user}
                        />
                    </div>

                    <div className="mt-6 d-flex flex-column gap-1 justify-content-center">
                        <Button
                          label="Mot de passe oublié ?"
                          type={Button_Type.LINK}
                          className="m-auto text-sm"
                          onClick={() => handleForgetPassword()}
                        />
                        <Button
                          label="Confirmation de votre email?"
                          type={Button_Type.LINK}
                          className="m-auto my-5 text-sm"
                          onClick={(e) => handleConfirmationEmail()}
                        />
                    </div>
                </form>
              )}
          </div>
      </>
    );
};

export default LoginForm;